import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { getPhotoImageData } from "../../store/task/photos.ts";
import { decodePhoto } from "../../utils/image_loading.ts";

export enum CompareMode {
    Full,
    Area,
    Horizontal,
    Vertical,
}

const fitSourceBoxIntoDestBox = (sw: number, sh: number, dw: number, dh: number) => {
    const innerR = sw / sh;
    const outterR = dw / dh;
    let w, h, x, y;
    if (innerR >= outterR) {
        // limited by width
        w = dw;
        h = w * sh / sw;
        x = 0;
        y = (dh - h) / 2;
    } else {
        // limited by height
        h = dh;
        w = h * sw / sh;
        y = 0;
        x = (dw - w) / 2;
    }
    return [x, y, w, h];
};

const getBoxBy2Point = (x1: number, y1: number, x2: number, y2: number) => {
    const x = Math.min(x1, x2);
    const y = Math.min(y1, y2);
    const w = Math.max(x1, x2) - x;
    const h = Math.max(y1, y2) - y;
    return [x, y, w, h];
};

const getSourceSize = (src: CanvasImageSource) => {
    if (src instanceof ImageBitmap) return [src.width, src.height];
    if (src instanceof HTMLImageElement) return [src.naturalWidth || src.width, src.naturalHeight || src.height];
    if (src instanceof HTMLVideoElement) return [src.videoWidth, src.videoHeight];
    if (src instanceof HTMLCanvasElement) return [src.width, src.height];
    if (typeof OffscreenCanvas !== "undefined" && src instanceof OffscreenCanvas) return [src.width, src.height];
    return [1, 1];
};

export const useComparePhotoCanvas = () => {
    const [isBusy, setIsBusy] = useState(false);
    const [hasImage1, setHasImage1] = useState(false);
    const [hasImage2, setHasImage2] = useState(false);
    const [image1Time, setImage1Time] = useState(0);
    const [image2Time, setImage2Time] = useState(0);
    
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const safezoneRef = useRef<HTMLDivElement | null>(null);
    const pointerAreaRef = useRef<HTMLDivElement | null>(null);
    const dataContainer = useRef({
        imageList: [null, null] as (CanvasImageSource | null)[],
        blobUrls: [null, null] as (string | null)[],
        requestSeq: [0, 0] as number[],
        loadingSeq: [0, 0] as number[],
        highAppliedSeq: [0, 0] as number[],
        abortControllers: [null, null] as (AbortController | null)[],
        compMode: CompareMode.Full,
        fullIndex: 0,
        p1: [0, 0],
        p2: [0, 0],
        moving: false,
    });

    useEffect(() => {
        return () => {
            dataContainer.current.requestSeq = dataContainer.current.requestSeq.map(seq => seq + 1);
            dataContainer.current.blobUrls.forEach(url => {
                if (url) URL.revokeObjectURL(url);
            });
            dataContainer.current.abortControllers.forEach(c => c?.abort());
        };
    }, []);

    const render = useCallback(() => {
        const d = dataContainer.current;
        // get layout info
        const canvas = canvasRef.current;
        if (!canvas) return;

        const canvasBox = canvas.getBoundingClientRect();
        
        const dpr = window.devicePixelRatio || 1;
        const displayWidth = Math.floor(canvasBox.width);
        const displayHeight = Math.floor(canvasBox.height);
        
        // Ensure canvas has non-zero size
        if (displayWidth === 0 || displayHeight === 0) return;

        // Set internal resolution to match display size * DPR for sharpness
        const targetWidth = displayWidth * dpr;
        const targetHeight = displayHeight * dpr;

        if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
            canvas.width = targetWidth;
            canvas.height = targetHeight;
        }
        
        // Logical width/height for calculations (CSS pixels)
        const baseWidth = displayWidth;
        const baseHeight = displayHeight;

        const cvctx = canvas.getContext("2d");
        if (!cvctx) return;
        
        // Reset transform and scale for DPR
        cvctx.setTransform(1, 0, 0, 1, 0, 0);
        cvctx.scale(dpr, dpr);

        // Clear with transparent black or specific background
        cvctx.clearRect(0, 0, baseWidth, baseHeight);

        const img1 = d.imageList[0];
        const img2 = d.imageList[1];
        if (!img1 && !img2) return;

        const src1 = img1 || img2!;
        const src2 = img2 || img1!;

        const [s1w, s1h] = getSourceSize(src1);
        const [s2w, s2h] = getSourceSize(src2);

        const fit1 = fitSourceBoxIntoDestBox(s1w, s1h, baseWidth, baseHeight);
        // fit1 is [x, y, w, h] relative to (0,0) of baseWidth/Height
        
        const fit2 = fitSourceBoxIntoDestBox(s2w, s2h, baseWidth, baseHeight);

        const clipY = fit1[1];
        const clipH = fit1[3];
        const clipX = fit1[0];
        const clipW = fit1[2];

        const drawSrc1 = () => {
            cvctx.drawImage(src1, fit1[0], fit1[1], fit1[2], fit1[3]);
        };
        const drawSrc2 = () => {
            cvctx.drawImage(src2, fit2[0], fit2[1], fit2[2], fit2[3]);
        };

        const b = getBoxBy2Point(d.p1[0], d.p1[1], d.p2[0], d.p2[1]);
        switch (d.compMode) {
            case CompareMode.Full:
                if (d.fullIndex) {
                    drawSrc2();
                } else {
                    drawSrc1();
                }
                break;
            case CompareMode.Area:
                drawSrc1();
                if (b[2] > 0 && b[3] > 0) {
                    cvctx.save();
                    cvctx.beginPath();
                    cvctx.rect(b[0], b[1], b[2], b[3]);
                    cvctx.clip();
                    drawSrc2();
                    cvctx.restore();
                }
                if (d.moving) {
                    cvctx.lineWidth = 2;
                    cvctx.strokeStyle = "#0F0";
                    cvctx.strokeRect(b[0], b[1], b[2], b[3]);
                    cvctx.strokeStyle = "#000";
                    cvctx.strokeRect(b[0] - 2, b[1] - 2, b[2] + 4, b[3] + 4);
                }
                break;
            case CompareMode.Horizontal:
                drawSrc1();
                if (d.p2[0] >= 0) {
                    const splitX = d.p2[0];
                    const w = baseWidth - splitX;
                    const h = baseHeight;
                    if (w > 0 && h > 0) {
                        cvctx.save();
                        cvctx.beginPath();
                        cvctx.rect(splitX, 0, w, h);
                        cvctx.clip();
                        drawSrc2();
                        cvctx.restore();
                    }
                    if (d.moving) {
                        cvctx.fillStyle = "#000";
                        cvctx.fillRect(splitX - 2, clipY, 4, clipH);
                        cvctx.fillStyle = "#0F0";
                        cvctx.fillRect(splitX - 1, clipY, 2, clipH);
                    }
                }
                break;
            case CompareMode.Vertical:
                drawSrc1();
                if (d.p2[1] >= 0) {
                    const splitY = d.p2[1];
                    const w = baseWidth;
                    const h = baseHeight - splitY;
                    if (w > 0 && h > 0) {
                        cvctx.save();
                        cvctx.beginPath();
                        cvctx.rect(0, splitY, w, h);
                        cvctx.clip();
                        drawSrc2();
                        cvctx.restore();
                    }
                    if (d.moving) {
                        cvctx.fillStyle = "#000";
                        cvctx.fillRect(clipX, splitY - 2, clipW, 4);
                        cvctx.fillStyle = "#0F0";
                        cvctx.fillRect(clipX, splitY - 1, clipW, 2);
                    }
                }
                break;
        }
    }, [dataContainer, canvasRef, safezoneRef]);

    const setImage = useCallback(async (idx: number, photoId: number, photoTime: number) => {
        const d = dataContainer.current;
        const seq = (d.requestSeq[idx] ?? 0) + 1;
        d.requestSeq[idx] = seq;
        d.loadingSeq[idx] = seq;
        d.highAppliedSeq[idx] = 0;
        d.abortControllers[idx]?.abort();
        const controller = new AbortController();
        d.abortControllers[idx] = controller;
        try {
            setIsBusy(true);
            
            const canvas = canvasRef.current;
            const rect = canvas?.getBoundingClientRect();
            const dpr = window.devicePixelRatio || 1;
            const needPixels = Math.max((rect?.width ?? 0) * dpr, (rect?.height ?? 0) * dpr);
            const lowResMode = "thumbnail";
            const highResMode = needPixels >= 2800 ? "4k" : (needPixels >= 1900 ? "view" : "video");

            const lowController = new AbortController();
            const cancelLow = () => lowController.abort();
            controller.signal.addEventListener("abort", cancelLow, { once: true });
            let applied = false;
            const load = async (mode: "thumbnail" | "video" | "view" | "4k", high: boolean) => {
                const signal = high ? controller.signal : lowController.signal;
                const buffer = await getPhotoImageData(photoId, mode, signal);
                signal.throwIfAborted();
                if (d.requestSeq[idx] !== seq || (!high && d.highAppliedSeq[idx] === seq)) return;
                const { image, url } = await decodePhoto(buffer, signal);
                if (controller.signal.aborted || d.requestSeq[idx] !== seq || (!high && d.highAppliedSeq[idx] === seq)) {
                    URL.revokeObjectURL(url);
                    return;
                }
                const oldUrl = d.blobUrls[idx];
                d.blobUrls[idx] = url;
                d.imageList[idx] = image;
                applied = true;
                if (high) d.highAppliedSeq[idx] = seq;
                if (idx === 0) {
                    setHasImage1(true);
                    setImage1Time(photoTime);
                }
                if (idx === 1) {
                    setHasImage2(true);
                    setImage2Time(photoTime);
                }
                render();
                if (oldUrl) URL.revokeObjectURL(oldUrl);
                if (high) cancelLow();
            };
            try {
                // Attach handlers to both requests immediately; one usable image is sufficient.
                const results = await Promise.allSettled([
                    load(lowResMode, false),
                    load(highResMode, true),
                ]);
                if (!applied && d.requestSeq[idx] === seq && !controller.signal.aborted) {
                    const failure = results.find(result => result.status === "rejected") as PromiseRejectedResult;
                    throw failure?.reason ?? new Error("Image load failed");
                }
            } finally {
                controller.signal.removeEventListener("abort", cancelLow);
            }
        } catch (e) {
            if (controller.signal.aborted || d.requestSeq[idx] !== seq) {
                return;
            }
            console.error("Failed to load image", e);
            alert(`Failed to load image: ${e}`);
        } finally {
            if (d.loadingSeq[idx] === seq) {
                d.loadingSeq[idx] = 0;
            }
            setIsBusy((d.loadingSeq[0] ?? 0) !== 0 || (d.loadingSeq[1] ?? 0) !== 0);
        }
    }, [dataContainer, render]);

    const clearImages = useCallback(() => {
        const d = dataContainer.current;
        d.requestSeq = d.requestSeq.map(seq => seq + 1);
        d.abortControllers.forEach(controller => controller?.abort());
        d.blobUrls.forEach(url => { if (url) URL.revokeObjectURL(url); });
        d.imageList = [null, null];
        d.blobUrls = [null, null];
        d.loadingSeq = [0, 0];
        setHasImage1(false);
        setHasImage2(false);
        setImage1Time(0);
        setImage2Time(0);
        setIsBusy(false);
        render();
    }, [render]);

    const setCompareMode = useCallback((mode: CompareMode) => {
        const d = dataContainer.current;
        d.compMode = mode;
        d.fullIndex = 0;
        d.p1 = [-1, -1];
        d.p2 = [-1, -1];
        render();
    }, [dataContainer, render]);

    const onPointerDown = useCallback((ev: PointerEvent) => {
        const parea = pointerAreaRef.current;
        const d = dataContainer.current;
        const canvas = canvasRef.current;
        if (!parea || !canvas) return;
        
        // Allow interaction if target is canvas or part of the safezone/background
        // Block if target is a button or interactive element in toolbar/header
        const target = ev.target as HTMLElement;
        if (target.closest("button") || target.closest("a") || target.closest(".farpov_header_nav_container")) return;

        if (!ev.isPrimary) return;
        
        // Use getBoundingClientRect for correct coordinates relative to the viewport
        const rect = canvas.getBoundingClientRect();
        const cvX = ev.clientX - rect.left;
        const cvY = ev.clientY - rect.top;
        
        d.p1 = [cvX, cvY];
        d.p2 = [cvX, cvY];
        d.moving = true;
        switch (d.compMode) {
            case CompareMode.Full:
                d.fullIndex = d.fullIndex ? 0 : 1;
                break;
        }
        render();
    }, [dataContainer, pointerAreaRef, canvasRef, render]);

    const onPointerMove = useCallback((ev: PointerEvent) => {
        const d = dataContainer.current;
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (!d.moving) return;
        if (!ev.isPrimary) return;
        
        // Use getBoundingClientRect for correct coordinates relative to the viewport
        const rect = canvas.getBoundingClientRect();
        const cvX = ev.clientX - rect.left;
        const cvY = ev.clientY - rect.top;
        
        d.p2 = [cvX, cvY];
        render();
    }, [dataContainer, pointerAreaRef, canvasRef, render]);

    const onPointerUp = useCallback((ev: PointerEvent) => {
        const d = dataContainer.current;
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (!d.moving) return;
        if (!ev.isPrimary) return;
        
        // Use getBoundingClientRect for correct coordinates relative to the viewport
        const rect = canvas.getBoundingClientRect();
        const cvX = ev.clientX - rect.left;
        const cvY = ev.clientY - rect.top;
        
        d.p2 = [cvX, cvY];
        d.moving = false;
        render();
    }, [dataContainer, pointerAreaRef, canvasRef, render]);

    useEffect(() => {
        // bind pointer event
        const parea = pointerAreaRef.current;
        if (!parea) return;
        const onDown = onPointerDown;
        const onMove = onPointerMove;
        const onUp = onPointerUp;
        parea.addEventListener("pointerdown", onDown);
        parea.addEventListener("pointermove", onMove);
        parea.addEventListener("pointerup", onUp);
        return () => {
            // unbind event
            parea.removeEventListener("pointerdown", onDown);
            parea.removeEventListener("pointermove", onMove);
            parea.removeEventListener("pointerup", onUp);
        };
    }, [onPointerDown, onPointerMove, onPointerUp]);

    useEffect(() => {
        const onResize = () => {
            // Just re-render on resize, don't reset state
            render();
        };
        globalThis.addEventListener("resize", onResize);
        return () => {
            globalThis.removeEventListener("resize", onResize);
        };
    }, [dataContainer, render]);

    return {
        render: render,
        setImage: setImage,
        clearImages,
        setCompareMode: setCompareMode,
        isBusy: isBusy,
        hasImage1: hasImage1,
        hasImage2: hasImage2,
        image1Time: image1Time,
        image2Time: image2Time,
        canvasRef: canvasRef,
        safezoneRef: safezoneRef,
        pointerAreaRef: pointerAreaRef,
    };
};
export default useComparePhotoCanvas;
